import { useEffect, useState } from 'react';
import { helpSystemData } from '~/mockData';
import {
  ContentArea,
  ModalBody,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  Overlay,
  SideNav,
  SideNavCategory,
  SideNavItem,
} from './HelpModal.styles';
import { ModalCloseButton } from './ModalCloseButton';

export interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Normalized title string used to pre-select the matching section. */
  helpKey: string;
}

function normalizeHelpKey(key: string): string {
  return key
    .trim()
    .toLowerCase()
    .replace(/[:\s]+$/, '');
}

function resolveInitialItem(helpKey: string): {
  categoryId: string;
  itemId: string;
} {
  const normalized = normalizeHelpKey(helpKey);
  for (const category of helpSystemData) {
    for (const item of category.items) {
      if (item.id === normalized) {
        return { categoryId: category.id, itemId: item.id };
      }
    }
  }
  // Fallback — first item in first category
  const fallbackCat = helpSystemData[0];
  return {
    categoryId: fallbackCat.id,
    itemId: fallbackCat.items[0]?.id ?? '',
  };
}

export function HelpModal({ isOpen, onClose, helpKey }: HelpModalProps) {
  const [selectedItemId, setSelectedItemId] = useState<string>(() => {
    const { itemId } = resolveInitialItem(helpKey);
    return itemId;
  });

  // Re-resolve when the modal opens with a new helpKey
  useEffect(() => {
    if (isOpen) {
      const { itemId } = resolveInitialItem(helpKey);
      setSelectedItemId(itemId);
    }
  }, [isOpen, helpKey]);

  if (!isOpen) return null;

  const { categoryId } = resolveInitialItem(helpKey);
  const activeCategory =
    helpSystemData.find((cat) => cat.id === categoryId) ?? helpSystemData[0];
  const selectedContent = activeCategory.items.find(
    (item) => item.id === selectedItemId,
  )?.content;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>WBS HELP SYSTEM</ModalTitle>
          <ModalCloseButton onClick={onClose} />
        </ModalHeader>

        <ModalBody>
          <SideNav>
            <SideNavCategory>{activeCategory.label}</SideNavCategory>
            {activeCategory.items.map((item) => (
              <SideNavItem
                key={item.id}
                $active={selectedItemId === item.id}
                onClick={() => setSelectedItemId(item.id)}
              >
                {item.label}
              </SideNavItem>
            ))}
          </SideNav>

          <ContentArea
            // biome-ignore lint/security/noDangerouslySetInnerHtml: help content is controlled mock/admin data
            dangerouslySetInnerHTML={{
              __html: selectedContent ?? '<p>No help content available.</p>',
            }}
          />
        </ModalBody>
      </ModalContainer>
    </Overlay>
  );
}
